import { useCallback, useRef } from 'react'
import type { CaseEndpoint } from '../../../types/case-container'
import { logger } from '../../../utils/logger'
import type { ModalsState } from './use-case-infrastructure-modals'

interface ActionsArgs {
  caseId: string
  endpoints: CaseEndpoint[]
  selectedIds: Set<string>
  selectMany: (ids: string[]) => void
  toggle: (id: string) => void
  deselect: (id: string) => void
  remove: (caseId: string, id: string) => Promise<boolean>
  refetch: () => Promise<void>
  addManyToMapSet: (ids: string[]) => void
  addManyToCollectionSet: (ids: string[]) => void
  isInCollectionSet: (id: string) => boolean
  addToCollectionSet: (id: string) => void
  removeFromCollectionSet: (id: string) => void
  modals: ModalsState
}

export interface ActionsState {
  lastClickedRef: React.MutableRefObject<string | null>
  handleCheckboxClick: (endpointId: string, event: React.MouseEvent) => void
  handleSelectAllVisible: () => void
  allVisibleSelected: boolean
  someVisibleSelected: boolean
  handleDeleteContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleDeleteSingle: (endpoint: CaseEndpoint) => void
  handleDeleteSelected: () => void
  handleBulkDelete: () => Promise<void>
  handleLinkButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleMapButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleCollectionButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleAddSelectedToMapSet: () => void
  handleAddSelectedToCollectionSet: () => void
  toggleCollectionSet: (endpointId: string) => void
}

interface SelectionHandlersArgs {
  endpoints: CaseEndpoint[]
  selectedIds: Set<string>
  selectMany: (ids: string[]) => void
  toggle: (id: string) => void
  deselect: (id: string) => void
}

interface SelectionHandlers {
  lastClickedRef: React.MutableRefObject<string | null>
  handleCheckboxClick: (endpointId: string, event: React.MouseEvent) => void
  handleSelectAllVisible: () => void
  allVisibleSelected: boolean
  someVisibleSelected: boolean
}

function useSelectionHandlers(args: SelectionHandlersArgs): SelectionHandlers {
  const { endpoints, selectedIds, selectMany, toggle, deselect } = args
  const lastClickedRef = useRef<string | null>(null)

  const handleCheckboxClick = useCallback(
    (endpointId: string, event: React.MouseEvent) => {
      if (event.shiftKey && lastClickedRef.current && lastClickedRef.current !== endpointId) {
        const lastIndex = endpoints.findIndex((ep) => ep.id === lastClickedRef.current)
        const currentIndex = endpoints.findIndex((ep) => ep.id === endpointId)
        if (lastIndex !== -1 && currentIndex !== -1) {
          const startIndex = Math.min(lastIndex, currentIndex)
          const endIndex = Math.max(lastIndex, currentIndex)
          const rangeIds = endpoints.slice(startIndex, endIndex + 1).map((ep) => ep.id)
          selectMany(rangeIds)
        }
      } else {
        toggle(endpointId)
      }
      lastClickedRef.current = endpointId
    },
    [endpoints, selectMany, toggle]
  )

  const handleSelectAllVisible = useCallback(() => {
    const allIds = endpoints.map((ep) => ep.id)
    const allSelected = allIds.every((id) => selectedIds.has(id))
    if (allSelected) {
      allIds.forEach((id) => {
        deselect(id)
      })
    } else {
      selectMany(allIds)
    }
  }, [endpoints, selectedIds, deselect, selectMany])

  const allVisibleSelected = endpoints.length > 0 && endpoints.every((ep) => selectedIds.has(ep.id))
  const someVisibleSelected = endpoints.some((ep) => selectedIds.has(ep.id)) && !allVisibleSelected

  return {
    lastClickedRef,
    handleCheckboxClick,
    handleSelectAllVisible,
    allVisibleSelected,
    someVisibleSelected,
  }
}

interface DeleteHandlersArgs {
  caseId: string
  endpoints: CaseEndpoint[]
  selectedIds: Set<string>
  deselect: (id: string) => void
  remove: (caseId: string, id: string) => Promise<boolean>
  refetch: () => Promise<void>
  modals: ModalsState
}

interface DeleteHandlers {
  handleDeleteContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleDeleteSingle: (endpoint: CaseEndpoint) => void
  handleDeleteSelected: () => void
  handleBulkDelete: () => Promise<void>
}

function useDeleteHandlers(args: DeleteHandlersArgs): DeleteHandlers {
  const { caseId, endpoints, selectedIds, deselect, remove, refetch, modals } = args

  const handleDeleteContextMenu = useCallback(
    (e: React.MouseEvent, endpoint: CaseEndpoint) => {
      e.preventDefault()
      e.stopPropagation()
      modals.setDeleteContextMenu({ x: e.clientX, y: e.clientY, endpoint })
    },
    [modals]
  )

  const handleDeleteSingle = useCallback(
    (endpoint: CaseEndpoint) => {
      modals.setDeleteConfirmation({ endpoints: [endpoint] })
    },
    [modals]
  )

  const handleDeleteSelected = useCallback(() => {
    const selectedEndpoints = endpoints.filter((ep) => selectedIds.has(ep.id))
    if (selectedEndpoints.length > 0) {
      modals.setDeleteConfirmation({ endpoints: selectedEndpoints })
    }
  }, [endpoints, selectedIds, modals])

  const handleBulkDelete = useCallback(async () => {
    if (!modals.deleteConfirmation) return
    modals.setBulkDeleting(true)
    let successCount = 0
    let failCount = 0
    for (const endpoint of modals.deleteConfirmation.endpoints) {
      const success = await remove(caseId, endpoint.id)
      if (success) {
        successCount++
        deselect(endpoint.id)
      } else {
        failCount++
      }
    }
    modals.setBulkDeleting(false)
    modals.setDeleteConfirmation(null)
    void refetch()
    if (failCount > 0) {
      logger.warn(
        'CaseInfrastructureTab',
        `[Bulk Delete] Completed: ${successCount.toString()} deleted, ${failCount.toString()} failed`
      )
    }
  }, [modals, caseId, remove, deselect, refetch])

  return { handleDeleteContextMenu, handleDeleteSingle, handleDeleteSelected, handleBulkDelete }
}

interface ContextMenuHandlers {
  handleLinkButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleMapButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleCollectionButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
}

function useContextMenuHandlers(modals: ModalsState): ContextMenuHandlers {
  const handleLinkButtonContextMenu = useCallback(
    (e: React.MouseEvent, endpoint: CaseEndpoint) => {
      e.preventDefault()
      modals.setContextMenu({ x: e.clientX, y: e.clientY, endpoint })
    },
    [modals]
  )

  const handleMapButtonContextMenu = useCallback(
    (e: React.MouseEvent, endpoint: CaseEndpoint) => {
      e.preventDefault()
      modals.setMapContextMenu({ x: e.clientX, y: e.clientY, endpoint })
    },
    [modals]
  )

  const handleCollectionButtonContextMenu = useCallback(
    (e: React.MouseEvent, endpoint: CaseEndpoint) => {
      e.preventDefault()
      modals.setCollectionContextMenu({ x: e.clientX, y: e.clientY, endpoint })
    },
    [modals]
  )

  return {
    handleLinkButtonContextMenu,
    handleMapButtonContextMenu,
    handleCollectionButtonContextMenu,
  }
}

interface SetHandlersArgs {
  selectedIds: Set<string>
  addManyToMapSet: (ids: string[]) => void
  addManyToCollectionSet: (ids: string[]) => void
  isInCollectionSet: (id: string) => boolean
  addToCollectionSet: (id: string) => void
  removeFromCollectionSet: (id: string) => void
}

interface SetHandlers {
  handleAddSelectedToMapSet: () => void
  handleAddSelectedToCollectionSet: () => void
  toggleCollectionSet: (endpointId: string) => void
}

function useSetHandlers(args: SetHandlersArgs): SetHandlers {
  const {
    selectedIds,
    addManyToMapSet,
    addManyToCollectionSet,
    isInCollectionSet,
    addToCollectionSet,
    removeFromCollectionSet,
  } = args

  const handleAddSelectedToMapSet = useCallback(() => {
    addManyToMapSet(Array.from(selectedIds))
  }, [selectedIds, addManyToMapSet])

  const handleAddSelectedToCollectionSet = useCallback(() => {
    addManyToCollectionSet(Array.from(selectedIds))
  }, [selectedIds, addManyToCollectionSet])

  const toggleCollectionSet = useCallback(
    (endpointId: string) => {
      if (isInCollectionSet(endpointId)) {
        removeFromCollectionSet(endpointId)
      } else {
        addToCollectionSet(endpointId)
      }
    },
    [isInCollectionSet, addToCollectionSet, removeFromCollectionSet]
  )

  return { handleAddSelectedToMapSet, handleAddSelectedToCollectionSet, toggleCollectionSet }
}

export function useCaseInfrastructureActions(args: ActionsArgs): ActionsState {
  const {
    caseId,
    endpoints,
    selectedIds,
    selectMany,
    toggle,
    deselect,
    remove,
    refetch,
    addManyToMapSet,
    addManyToCollectionSet,
    isInCollectionSet,
    addToCollectionSet,
    removeFromCollectionSet,
    modals,
  } = args

  const selection = useSelectionHandlers({ endpoints, selectedIds, selectMany, toggle, deselect })
  const deleteOps = useDeleteHandlers({
    caseId,
    endpoints,
    selectedIds,
    deselect,
    remove,
    refetch,
    modals,
  })
  const contextMenus = useContextMenuHandlers(modals)
  const sets = useSetHandlers({
    selectedIds,
    addManyToMapSet,
    addManyToCollectionSet,
    isInCollectionSet,
    addToCollectionSet,
    removeFromCollectionSet,
  })

  return { ...selection, ...deleteOps, ...contextMenus, ...sets }
}
